/* eslint-disable @typescript-eslint/ban-ts-comment */
import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';

import { WINDOW } from '@frontend/vanilla/core';

enum ScrollDirection {
    Left = 'Left',
    Right = 'Right',
    Top = 'Top',
    Bottom = 'Bottom',
}

@Injectable({ providedIn: 'root' })
export class SmoothScroll {
    private zone = inject(NgZone);

    private readonly _doc = inject(DOCUMENT);
    private readonly _window = inject(WINDOW);

    private requestAnimationFrameFn: (callback: FrameRequestCallback) => void =
        (this._window.requestAnimationFrame?.bind(this._window) as (callback: FrameRequestCallback) => void) || this.fallbackFn;

    private fallbackFn(callback: FrameRequestCallback): void {
        callback(0);
    }

    scrollLeftTo(el: HTMLElement, target: number, duration: number, forceDuration = false): void {
        this.smoothScrollTo(el, target, duration, ScrollDirection.Left, forceDuration);
    }

    scrollTopTo(el: HTMLElement, target: number, duration: number, forceDuration = false): void {
        this.smoothScrollTo(el, target, duration, ScrollDirection.Top, forceDuration);
    }

    private smoothScrollTo(element: Element, target: number, duration: number, direction: ScrollDirection, forceDuration: boolean): void {
        if ('scrollBehavior' in this._doc.documentElement.style && !forceDuration) {
            const scrollToOptions: ScrollToOptions = {
                behavior: 'smooth',
            };
            //TODO any index issue, probably a defect, please investigate
            //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
            scrollToOptions[direction.toLowerCase()] = target;
            element.scroll(scrollToOptions);
        } else {
            this.doStepScrolling(element, target, duration, direction);
        }
    }

    /* istanbul ignore next */
    private doStepScrolling(element: Element, target: number, duration: number, direction: ScrollDirection): void {
        const scrollElement = element;
        const roundedTarget = Math.round(target);
        const roundedDuration = Math.round(duration);
        const scrollDirection = `scroll${direction}`;

        if (duration < 0) {
            return;
        }
        if (duration === 0) {
            //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
            scrollElement[scrollDirection] = roundedTarget;
            return;
        }
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.

        if (!scrollElement || isNaN(scrollElement[scrollDirection])) {
            return;
        }

        const startTime = Date.now();
        const endTime = startTime + roundedDuration;
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
        const startPos = scrollElement[scrollDirection] as number;
        const previousPos = startPos;
        const distance = roundedTarget - startPos;

        // boostrap the animation process
        this.runRafOutsideZone(() => this.scrollFrame(true, startTime, endTime, startPos, previousPos, scrollElement, distance, scrollDirection));
    }

    // This is like a think function from a game loop
    private scrollFrame(
        firstRun: boolean,
        startTime: number,
        endTime: number,
        startPos: number,
        previousPos: number,
        element: Element,
        distance: number,
        scrollDirection: string,
    ): void {
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
        if (!firstRun && element && element[scrollDirection] !== previousPos) {
            return;
        }

        // firstRun = false;
        // set the scroll value for this frame
        const now = Date.now();
        const point = this.smoothStep(startTime, endTime, now);
        const frameTop = Math.round(startPos + distance * point);
        const scrollElement = element;
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
        scrollElement[scrollDirection] = frameTop;

        // check if we're done!
        if (now >= endTime) {
            return;
        }

        // If we were supposed to scroll but didn't, then we
        // probably hit the limit, so consider it done
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
        if (scrollElement[scrollDirection] === previousPos && scrollElement[scrollDirection] !== frameTop) {
            // return;
        }
        //@ts-ignore-error allow dynamic accessor, please don't use this, instead, use typing.
        const currentPreviousPos = scrollElement[scrollDirection] as number;

        // schedule next frame for execution
        this.runRafOutsideZone(() =>
            this.scrollFrame(false, startTime, endTime, startPos, currentPreviousPos, scrollElement, distance, scrollDirection),
        );
    }

    // based on http://en.wikipedia.org/wiki/Smoothstep
    private smoothStep(start: number, end: number, point: number): number {
        if (point <= start) {
            return 0;
        }
        if (point >= end) {
            return 1;
        }
        const x = (point - start) / (end - start); // interpolation

        return x * x * (3 - 2 * x);
    }

    private runRafOutsideZone(callback: FrameRequestCallback): void {
        this.zone.runOutsideAngular(() => this.requestAnimationFrameFn(callback));
    }
}
